import BrandName from "./BrandName";
import { brandText } from "../brand";
import React, { useEffect, useState } from "react";
import MenuImage from "./MenuImage";
import { EVENT_CATEGORIES, categoryLabel } from "../data/event-categories";

export const calendarTimeLabel = (event) => {
  if (event.allDay) return "Throughout service";
  const format = (value) => {
    if (!value) return "Start time";
    const [hour, minute] = value.split(":");
    return `${Number(hour) % 12 || 12}${minute === "00" ? "" : `:${minute}`}${Number(hour) < 12 ? "am" : "pm"}`;
  };
  return event.endTime
    ? `${format(event.startTime)}–${format(event.endTime)}`
    : `From ${format(event.startTime)}`;
};

const londonToday = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(
    new Date(),
  );
const fromKey = (key) => new Date(`${key}T12:00:00Z`);
const dateKey = (date) => date.toISOString().slice(0, 10);
const dateLabel = (date, options) =>
  date.toLocaleDateString("en-GB", { ...options, timeZone: "UTC" });
export const eventsOnDate = (events, key) =>
  events.filter((event) =>
    event.scheduleMode === "dates"
      ? event.dates?.includes(key)
      : event.startDate <= key &&
        event.endDate >= key &&
        (!event.weekdays?.length ||
          event.weekdays.includes(fromKey(key).getUTCDay())),
  );
export function safeEventUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}
const eventStyle = (event) =>
  event.categories.includes("live-music")
    ? "music"
    : event.categories.some((category) => category.startsWith("promotional-"))
      ? "menu"
      : "other";

function EventCard({ event, status }) {
  return (
    <article className={`event-card event-${eventStyle(event)}`}>
      <MenuImage image={event.image} alt={brandText(event.name)} />
      <div className="event-card-top">
        <span>{event.categories.map(categoryLabel).join(" · ")}</span>
      </div>
      {status && <p className="event-status">{status}</p>}
      <h3>{brandText(event.name)}</h3>
      <p>{calendarTimeLabel(event)}</p>
      {event.scheduleMode !== "dates" && event.startDate !== event.endDate && (
        <p className="event-date-range">
          {dateLabel(fromKey(event.startDate), {
            day: "numeric",
            month: "short",
          })}{" "}
          –{" "}
          {dateLabel(fromKey(event.endDate), {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
          {event.weekdays?.length ? " · Selected weekdays" : ""}
        </p>
      )}
      <details>
        <summary>
          Event details <span aria-hidden="true">+</span>
        </summary>
        <p className="event-description">{brandText(event.description)}</p>
        {safeEventUrl(event.url) && (
          <a
            className="event-url"
            href={safeEventUrl(event.url)}
            target="_blank"
            rel="noopener noreferrer"
          >
            More information <span aria-hidden="true">↗</span>
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        )}
      </details>
    </article>
  );
}

const shortDate = (key) =>
  dateLabel(fromKey(key), { day: "numeric", month: "short" });
function scheduleSummary(event) {
  if (event.startDate === event.endDate) return "";
  const range = `${shortDate(event.startDate)} – ${shortDate(event.endDate)}`;
  if (event.scheduleMode === "dates") return `Selected dates · ${range}`;
  const days = [...new Set(event.weekdays || [])].sort((a, b) => a - b);
  if (!days.length || days.length === 7) return `Daily · ${range}`;
  const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const consecutive = days.every(
    (day, index) => index === 0 || day === days[index - 1] + 1,
  );
  const repeat =
    consecutive && days.length > 2
      ? `${labels[days[0]]}–${labels[days[days.length - 1]]}`
      : days.map((day) => labels[day]).join(", ");
  return `${repeat} · ${range}`;
}

function EventListItem({ event, status }) {
  const schedule =
    eventStyle(event) === "menu" ||
    (!event.weekdays?.length && event.scheduleMode !== "dates")
      ? scheduleSummary(event)
      : "";
  const description = brandText(event.description).trim().replace(/\s+/g, " ");
  const excerpt =
    description.length > 120
      ? `${description.slice(0, 117).replace(/\s+\S*$/, "")}…`
      : description;
  return (
    <article className={`event-list-item event-list-${eventStyle(event)}`}>
      <div className="event-list-meta">
        <span>{event.categories.map(categoryLabel).join(" · ")}</span>
        <span>{calendarTimeLabel(event)}</span>
        {status && <span className="event-list-status">{status}</span>}
      </div>
      <h3>{brandText(event.name)}</h3>
      {schedule && <p className="event-list-schedule">{schedule}</p>}
      {excerpt && <p className="event-list-excerpt">{excerpt}</p>}
      <details>
        <summary>
          Details <span aria-hidden="true">+</span>
        </summary>
        <p className="event-description">{brandText(event.description)}</p>
        {event.scheduleMode === "dates" && (
          <p className="event-list-dates">
            Dates: {event.dates.map(shortDate).join(", ")}
          </p>
        )}
        {safeEventUrl(event.url) && (
          <a
            className="event-url"
            href={safeEventUrl(event.url)}
            target="_blank"
            rel="noopener noreferrer"
          >
            More information ↗
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        )}
      </details>
    </article>
  );
}

// Promotions appear once with their schedule; music and other events retain each date.
export function eventList(events, monthKey, today) {
  const first = `${monthKey}-01`;
  const date = fromKey(first);
  const last = dateKey(
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 12)),
  );
  const currentMonth = today.slice(0, 7) === monthKey;
  const lower = currentMonth ? today : first;
  const onNow = [];
  const groups = new Map();
  const add = (key, event, status = "") => {
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ event, status });
  };
  const endLabel = (event) => {
    const remaining = (fromKey(event.endDate) - fromKey(today)) / 86400000;
    if (remaining === 0) return "Ends today";
    if (remaining === 1) return "Ends tomorrow";
    if (remaining <= 6)
      return `Ends ${dateLabel(fromKey(event.endDate), { weekday: "long" })}`;
    return `Until ${dateLabel(fromKey(event.endDate), { day: "numeric", month: "short" })}`;
  };
  for (const event of events) {
    if (event.endDate < lower || event.startDate > last) continue;
    const repeatingPromotion =
      eventStyle(event) === "menu" && event.startDate !== event.endDate;
    if (
      repeatingPromotion &&
      (event.scheduleMode === "dates" || event.weekdays?.length)
    ) {
      const occurrences = [];
      for (
        let day = fromKey(lower);
        dateKey(day) <= last;
        day.setUTCDate(day.getUTCDate() + 1)
      ) {
        const key = dateKey(day);
        if (eventsOnDate([event], key).length) occurrences.push(key);
      }
      if (!occurrences.length) continue;
      if (currentMonth && occurrences[0] === today && event.startDate < today) {
        // Determine the actual final service date, including excluded weekdays.
        let final = event.endDate;
        if (event.scheduleMode === "dates")
          final = [...event.dates].sort().at(-1);
        else {
          const day = fromKey(final);
          while (!eventsOnDate([event], dateKey(day)).length)
            day.setUTCDate(day.getUTCDate() - 1);
          final = dateKey(day);
        }
        onNow.push({ event, status: endLabel({ ...event, endDate: final }) });
      } else {
        add(
          occurrences[0],
          event,
          event.startDate < first ? "Continues this month" : "",
        );
      }
    } else if (event.scheduleMode === "dates") {
      for (const key of [...new Set(event.dates || [])].sort()) {
        if (key >= lower && key <= last) add(key, event);
      }
    } else if (event.weekdays?.length) {
      for (
        let day = fromKey(lower);
        dateKey(day) <= last;
        day.setUTCDate(day.getUTCDate() + 1)
      ) {
        const key = dateKey(day);
        if (eventsOnDate([event], key).length) add(key, event);
      }
    } else if (
      event.startDate !== event.endDate &&
      currentMonth &&
      event.startDate <= today
    ) {
      onNow.push({
        event,
        status:
          event.startDate === today
            ? `Starts today · ${endLabel(event)}`
            : endLabel(event),
      });
    } else {
      add(
        event.startDate < lower ? lower : event.startDate,
        event,
        event.startDate < first
          ? "Continues this month"
          : event.startDate === today
            ? "Starts today"
            : "",
      );
    }
  }
  const sort = (a, b) =>
    (a.event.allDay ? "" : a.event.startTime || "").localeCompare(
      b.event.allDay ? "" : b.event.startTime || "",
    ) ||
    a.event.name.localeCompare(b.event.name) ||
    a.event.id.localeCompare(b.event.id);
  return {
    onNow: onNow.sort(sort),
    groups: [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, entries]) => ({ date, entries: entries.sort(sort) })),
  };
}

export default function EventsCalendar({
  events: suppliedEvents,
  initialDate = londonToday(),
}) {
  const [selected, setSelected] = useState(() => {
    const month = new URLSearchParams(window.location.search).get("month");
    return fromKey(
      /^20\d{2}-(0[1-9]|1[0-2])$/.test(month || "")
        ? `${month}-01`
        : initialDate,
    );
  });
  const [month, setMonth] = useState(
    () =>
      new Date(
        Date.UTC(selected.getUTCFullYear(), selected.getUTCMonth(), 1, 12),
      ),
  );
  const [linkedCategory, setLinkedCategory] = useState(() => {
    const category = new URLSearchParams(window.location.search).get(
      "category",
    );
    return EVENT_CATEGORIES.some((entry) => entry.id === category)
      ? category
      : null;
  });
  const [view, setView] = useState(() =>
    new URLSearchParams(window.location.search).get("view") === "calendar"
      ? "calendar"
      : "list",
  );
  const [filter, setFilter] = useState(linkedCategory || "all");
  const selectFilter = (category) => {
    setLinkedCategory(null);
    setFilter(category);
  };
  const [refresh, setRefresh] = useState(0);
  const [state, setState] = useState({
    month: "",
    events: [],
    loading: true,
    error: "",
  });
  const monthKey = dateKey(month).slice(0, 7);
  useEffect(() => {
    if (suppliedEvents) return;
    const controller = new AbortController();
    let active = true;
    const load = async () => {
      setState({ month: monthKey, events: [], loading: true, error: "" });
      try {
        const response = await fetch(`/api/events?month=${monthKey}`, {
          credentials: "omit",
          cache: "no-store",
          signal: controller.signal,
        });
        const body = await response.json();
        if (
          !response.ok ||
          !body.success ||
          body.data?.month !== monthKey ||
          !Array.isArray(body.data?.events) ||
          body.data.events.some(
            (event) =>
              typeof event.id !== "string" ||
              typeof event.name !== "string" ||
              typeof event.description !== "string" ||
              !Array.isArray(event.categories) ||
              event.categories.some(
                (id) =>
                  !EVENT_CATEGORIES.some((category) => category.id === id),
              ) ||
              !Array.isArray(event.weekdays) ||
              (event.scheduleMode !== undefined &&
                !["range", "dates"].includes(event.scheduleMode)) ||
              (event.scheduleMode === "dates" &&
                (!Array.isArray(event.dates) ||
                  !event.dates.length ||
                  event.dates.length > 366 ||
                  event.dates.some(
                    (date) => !/^20\d{2}-\d{2}-\d{2}$/.test(date),
                  ))) ||
              !/^20\d{2}-\d{2}-\d{2}$/.test(event.startDate) ||
              !/^20\d{2}-\d{2}-\d{2}$/.test(event.endDate),
          )
        )
          throw new Error(
            "The calendar is temporarily unavailable. Please try again.",
          );
        if (active)
          setState({
            month: monthKey,
            events: body.data.events,
            loading: false,
            error: "",
          });
      } catch (error) {
        if (active && error.name !== "AbortError")
          setState({
            month: monthKey,
            events: [],
            loading: false,
            error: "The calendar is temporarily unavailable. Please try again.",
          });
      }
    };
    load();
    const interval = window.setInterval(load, 60000);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(interval);
    };
  }, [monthKey, refresh, suppliedEvents]);
  const events =
    suppliedEvents || (state.month === monthKey ? state.events : []);
  const loading =
    !suppliedEvents && (state.loading || state.month !== monthKey);
  const error = !suppliedEvents && state.month === monthKey ? state.error : "";
  const monthSchedule = eventList(events, monthKey, `${monthKey}-01`);
  const monthEvents = [
    ...monthSchedule.onNow,
    ...monthSchedule.groups.flatMap((group) => group.entries),
  ].map((entry) => entry.event);
  const availableCategories = EVENT_CATEGORIES.filter((category) =>
    monthEvents.some((event) => event.categories.includes(category.id)),
  );
  const availableFilterIds = availableCategories
    .map((category) => category.id)
    .join(",");
  const activeFilter =
    linkedCategory ||
    (availableCategories.some((category) => category.id === filter)
      ? filter
      : "all");
  useEffect(() => {
    if (
      !loading &&
      !linkedCategory &&
      filter !== "all" &&
      !availableFilterIds.split(",").includes(filter)
    ) {
      setFilter("all");
    }
  }, [loading, filter, availableFilterIds, linkedCategory]);
  const filtered = events.filter(
    (event) =>
      activeFilter === "all" || event.categories.includes(activeFilter),
  );
  const list = eventList(filtered, monthKey, initialDate);
  const selectedEvents = eventsOnDate(filtered, dateKey(selected));
  const offset = (month.getUTCDay() + 6) % 7;
  const days = new Date(
    Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const moveMonth = (step) => {
    const next = new Date(
      Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + step, 1, 12),
    );
    if (next.getUTCFullYear() < 2000 || next.getUTCFullYear() > 2099) return;
    setMonth(next);
    setSelected(next);
  };
  return (
    <section
      className="home-events"
      id="whats-on"
      aria-labelledby="events-title"
    >
      <div className="home-events-heading">
        <div>
          <p className="home-eyebrow">02 / EVENTS & PROMOTIONS</p>
          <h2 id="events-title">
            What’s on <em>at <BrandName /></em>
          </h2>
        </div>
        <p>
          Live music, special menus and events.
          <br />
          All times are local to Hexham.
        </p>
      </div>
      {availableCategories.length > 0 && (
        <div className="events-filters" role="group" aria-label="Filter events">
          {[{ id: "all", label: "All events" }, ...availableCategories].map(
            (category) => (
              <button
                key={category.id}
                aria-pressed={activeFilter === category.id}
                onClick={() => selectFilter(category.id)}
              >
                {category.label}
              </button>
            ),
          )}
        </div>
      )}
      {error && (
        <div className="events-error" role="alert">
          {error}{" "}
          <button onClick={() => setRefresh((value) => value + 1)}>
            Try again
          </button>
        </div>
      )}
      <div className="events-view-toolbar">
        <div
          className="events-view-toggle"
          role="group"
          aria-label="Event view"
        >
          {["list", "calendar"].map((option) => (
            <button
              key={option}
              aria-pressed={view === option}
              onClick={() => setView(option)}
            >
              {option === "list" ? "List" : "Calendar"}
            </button>
          ))}
        </div>
        <div className="events-month">
          <h3 aria-live="polite">
            {dateLabel(month, { month: "long", year: "numeric" })}
          </h3>
          <div>
            <button
              aria-label="Previous month"
              disabled={monthKey === "2000-01"}
              onClick={() => moveMonth(-1)}
            >
              ←
            </button>
            <button
              aria-label="Next month"
              disabled={monthKey === "2099-12"}
              onClick={() => moveMonth(1)}
            >
              →
            </button>
          </div>
        </div>
      </div>
      {view === "list" ? (
        <div className="events-list" aria-busy={loading} aria-live="polite">
          <p className="events-list-note">
            {monthKey === initialDate.slice(0, 7)
              ? "From today to the end of this month."
              : "Events in the selected month."}{" "}
            Use the arrows to browse other months.
          </p>
          {loading ? (
            <p role="status">Loading events…</p>
          ) : error ? (
            <p>Event details will appear when the calendar is available.</p>
          ) : (
            <>
              {list.onNow.length > 0 && (
                <section
                  className="events-list-section events-ongoing"
                  aria-labelledby="events-on-now"
                >
                  <h3 id="events-on-now" className="events-list-heading">
                    On now
                  </h3>
                  <div className="events-list-cards">
                    {list.onNow.map(({ event, status }) => (
                      <EventListItem
                        key={event.id}
                        event={event}
                        status={status}
                      />
                    ))}
                  </div>
                </section>
              )}
              {list.groups.length > 0 && (
                <section
                  className="events-list-section"
                  aria-labelledby="events-upcoming"
                >
                  <h3 id="events-upcoming" className="events-list-heading">
                    {monthKey < initialDate.slice(0, 7)
                      ? "Events this month"
                      : "Upcoming"}
                  </h3>
                  {list.groups.map((group) => (
                    <section
                      className="events-date-group"
                      key={group.date}
                      aria-label={dateLabel(fromKey(group.date), {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      })}
                    >
                      <h4
                        aria-label={dateLabel(fromKey(group.date), {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        })}
                      >
                        <time dateTime={group.date}>
                          <span>
                            {dateLabel(fromKey(group.date), {
                              weekday: "short",
                            })}
                          </span>
                          <strong>
                            {dateLabel(fromKey(group.date), { day: "numeric" })}
                          </strong>
                          <span>
                            {dateLabel(fromKey(group.date), { month: "short" })}
                          </span>
                        </time>
                      </h4>
                      <div className="events-list-cards">
                        {group.entries.map(({ event, status }) => (
                          <EventListItem
                            key={event.id}
                            event={event}
                            status={status}
                          />
                        ))}
                      </div>
                    </section>
                  ))}
                </section>
              )}
              {!list.onNow.length && !list.groups.length && (
                <div className="events-empty">
                  <h3>No upcoming events in this selection.</h3>
                  <p>
                    Try another month
                    {activeFilter !== "all"
                      ? " or show all event categories"
                      : ""}
                    .
                  </p>
                  {activeFilter !== "all" && (
                    <button onClick={() => selectFilter("all")}>
                      Show all events
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="events-layout" aria-busy={loading}>
          <div className="events-calendar">
            <div className="events-days">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <span className="events-weekday" key={day}>
                  {day}
                </span>
              ))}
              {Array.from({ length: offset }, (_, i) => (
                <span className="events-blank" key={`blank-${i}`} />
              ))}
              {Array.from({ length: days }, (_, i) => {
                const date = new Date(
                  Date.UTC(
                    month.getUTCFullYear(),
                    month.getUTCMonth(),
                    i + 1,
                    12,
                  ),
                );
                const key = dateKey(date);
                const matches = eventsOnDate(filtered, key);
                return (
                  <button
                    key={key}
                    className={`events-day ${dateKey(selected) === key ? "is-selected" : ""}`}
                    aria-pressed={dateKey(selected) === key}
                    aria-label={`${dateLabel(date, { day: "numeric", month: "long", year: "numeric" })}, ${loading ? "loading events" : error ? "events unavailable" : `${matches.length} ${matches.length === 1 ? "event" : "events"}`}`}
                    onClick={() => setSelected(date)}
                  >
                    <span>{i + 1}</span>
                    <span className="events-dots" aria-hidden="true">
                      {["music", "menu", "other"].map(
                        (kind) =>
                          matches.some(
                            (event) => eventStyle(event) === kind,
                          ) && <i key={kind} className={`${kind}-dot`} />,
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="events-legend">
              <span>
                <i className="music-dot" />
                Live music
              </span>
              <span>
                <i className="menu-dot" />
                Promotions
              </span>
              <span>
                <i className="other-dot" />
                Other events
              </span>
            </div>
          </div>
          <div className="events-agenda" aria-live="polite" aria-atomic="true">
            <p className="home-eyebrow">
              {dateLabel(selected, {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </p>
            {loading ? (
              <p role="status">Loading events…</p>
            ) : error ? (
              <p>Event details will appear when the calendar is available.</p>
            ) : selectedEvents.length ? (
              selectedEvents.map((event) => (
                <EventCard event={event} key={`${monthKey}:${event.id}`} />
              ))
            ) : (
              <div className="events-empty">
                <h3>No events on this date.</h3>
                <p>
                  {activeFilter === "all"
                    ? "Select another date to see what’s on."
                    : "Try another date or show all event categories."}
                </p>
                {activeFilter !== "all" && (
                  <button onClick={() => selectFilter("all")}>
                    Show all events
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
